import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { translations } from '../lib/i18n';
import type { Language } from '../lib/i18n';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
  userLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, userLanguage }) => {
  const [language, setLanguageState] = useState<Language>('en');

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('nestq-language', lang);
  };

  useEffect(() => {
    if (userLanguage) {
      setLanguageState(userLanguage);
    } else {
      // Check localStorage first
      const savedLanguage = localStorage.getItem('nestq-language') as Language;
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'sl')) {
        setLanguageState(savedLanguage);
      } else {
        // Detect browser language
        const browserLang = navigator.language.split('-')[0];
        const detectedLang = browserLang === 'sl' ? 'sl' : 'en';
        setLanguageState(detectedLang);
      }
    }
  }, [userLanguage]);

  const value = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
